export type DualMontagePair = {
  positive: string;
  negative?: string;
  label?: string;
};

export type DualMontageChannelSeries = {
  name: string;
  data: ArrayLike<number>;
};

export type DualMontageDerivedLane = {
  label: string;
  samples: number[];
  uniqueness: "shared" | "left-only" | "right-only";
};

function normalizeName(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

function pairLabel(pair: DualMontagePair, index: number): string {
  if (pair.label?.trim()) return pair.label.trim();
  if (pair.negative) return `${pair.positive}-${pair.negative}`;
  return pair.positive || `Ch ${index + 1}`;
}

export function dualMontagePairLabels(pairs: DualMontagePair[]): string[] {
  return pairs.map((pair, index) => pairLabel(pair, index));
}

export function computeDualMontageLabelDiff(
  leftPairs: DualMontagePair[],
  rightPairs: DualMontagePair[],
): {
  shared: Set<string>;
  leftOnly: Set<string>;
  rightOnly: Set<string>;
} {
  const left = new Set(dualMontagePairLabels(leftPairs));
  const right = new Set(dualMontagePairLabels(rightPairs));
  const shared = new Set<string>();
  const leftOnly = new Set<string>();
  const rightOnly = new Set<string>();
  left.forEach((label) => {
    if (right.has(label)) shared.add(label);
    else leftOnly.add(label);
  });
  right.forEach((label) => {
    if (!left.has(label)) rightOnly.add(label);
  });
  return { shared, leftOnly, rightOnly };
}

function findChannelSeries(
  channels: DualMontageChannelSeries[],
  name: string,
): DualMontageChannelSeries | undefined {
  const target = normalizeName(name);
  return channels.find((channel) => normalizeName(channel.name || "") === target);
}

/** Derive bipolar/referential lanes for one montage over shared raw channels. */
export function deriveDualMontageLanes(params: {
  pairs: DualMontagePair[];
  channels: DualMontageChannelSeries[];
  uniquenessByLabel?: Map<string, DualMontageDerivedLane["uniqueness"]>;
}): DualMontageDerivedLane[] {
  const { pairs, channels, uniquenessByLabel } = params;
  return pairs.map((pair, index) => {
    const label = pairLabel(pair, index);
    const positive = findChannelSeries(channels, pair.positive);
    const negative = pair.negative
      ? findChannelSeries(channels, pair.negative)
      : undefined;
    const length = Math.max(
      positive?.data.length ?? 0,
      negative?.data.length ?? 0,
      0,
    );
    const samples = new Array<number>(length);
    for (let i = 0; i < length; i += 1) {
      const pos = positive?.data[i] ?? 0;
      const neg = negative ? (negative.data[i] ?? 0) : 0;
      samples[i] = pair.negative ? pos - neg : pos;
    }
    return {
      label,
      samples,
      uniqueness: uniquenessByLabel?.get(label) ?? "shared",
    };
  });
}

export function buildDualMontageUniquenessMap(
  leftPairs: DualMontagePair[],
  rightPairs: DualMontagePair[],
): {
  left: Map<string, DualMontageDerivedLane["uniqueness"]>;
  right: Map<string, DualMontageDerivedLane["uniqueness"]>;
} {
  const diff = computeDualMontageLabelDiff(leftPairs, rightPairs);
  const left = new Map<string, DualMontageDerivedLane["uniqueness"]>();
  const right = new Map<string, DualMontageDerivedLane["uniqueness"]>();
  dualMontagePairLabels(leftPairs).forEach((label) => {
    left.set(label, diff.leftOnly.has(label) ? "left-only" : "shared");
  });
  dualMontagePairLabels(rightPairs).forEach((label) => {
    right.set(label, diff.rightOnly.has(label) ? "right-only" : "shared");
  });
  return { left, right };
}
